import {
  ISupplier,
  ISupplierWithOrderCount,
  SuppliersClient,
  SupplierType,
} from "../clients/SuppliersClient";
import { create } from "zustand";

export type SuppliersStoreState = {
  suppliers: ISupplier[];
  loading: boolean;
  error: Error | null;
  getSuppliersByOrganizationId: (
    organization_id: string,
    useLoading?: boolean
  ) => Promise<void>;
  getSupplierById: (supplier_id: string) => Promise<ISupplier | null>;
  createSupplier: (
    supplier: Omit<ISupplier, "id" | "created_at" | "updated_at" | "deleted_at">
  ) => Promise<ISupplier>;
  updateSupplier: (
    supplier: Omit<ISupplier, "created_at" | "updated_at" | "deleted_at">
  ) => Promise<void>;
  deleteSupplier: (supplier_id: string) => Promise<void>;
  findOrCreateSupplier: (supplierData: {
    organization_id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    external_id?: string | null;
    supplier_type?: SupplierType;
    metadata?: Record<string, string | number | boolean | null | undefined>;
  }) => Promise<ISupplier>;
  searchSuppliers: (
    organization_id: string,
    searchTerm: string
  ) => Promise<ISupplier[]>;
  getSuppliersWithOrderStats: (
    organization_id: string
  ) => Promise<ISupplierWithOrderCount[]>;
};

export const useSuppliersStore = create<SuppliersStoreState>((set, get) => ({
  suppliers: [],
  loading: true,
  error: null,

  async getSuppliersByOrganizationId(
    organization_id: string,
    useLoading = true
  ) {
    if (useLoading) {
      set({ loading: true });
    }
    try {
      const suppliers = await SuppliersClient.getSuppliersByOrganizationId(
        organization_id
      );
      set({ suppliers, error: null });
    } catch (error) {
      console.error("Error loading suppliers:", error);
      set({ error: error as Error });
    } finally {
      if (useLoading) {
        set({ loading: false });
      }
    }
  },

  async getSupplierById(supplier_id: string): Promise<ISupplier | null> {
    try {
      // First check if we have it in the store
      const existingSupplier = get().suppliers.find((s) => s.id === supplier_id);
      if (existingSupplier) {
        return existingSupplier;
      }

      // If not, fetch from API
      const supplier = await SuppliersClient.getSupplierById(supplier_id);
      set((state) => ({
        ...state,
        suppliers: [
          supplier,
          ...state.suppliers.filter((s) => s.id !== supplier_id),
        ],
      }));
      return supplier;
    } catch (error) {
      console.error("Error fetching supplier:", error);
      set({ error: error as Error });
      return null;
    }
  },

  async createSupplier(
    supplier: Omit<ISupplier, "id" | "created_at" | "updated_at" | "deleted_at">
  ): Promise<ISupplier> {
    set({ loading: true });
    try {
      const newSupplier = await SuppliersClient.createSupplier(supplier);
      set((state) => ({
        ...state,
        suppliers: [newSupplier, ...state.suppliers],
        error: null,
      }));
      return newSupplier;
    } catch (error) {
      console.error("Error creating supplier:", error);
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  async updateSupplier(
    supplier: Omit<ISupplier, "created_at" | "updated_at" | "deleted_at">
  ) {
    set({ loading: true });
    try {
      const updatedSupplier = await SuppliersClient.updateSupplier(supplier);
      set((state) => ({
        ...state,
        suppliers: state.suppliers.map((s) =>
          s.id === updatedSupplier.id ? updatedSupplier : s
        ),
        error: null,
      }));
    } catch (error) {
      console.error("Error updating supplier:", error);
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  async deleteSupplier(supplier_id: string) {
    set({ loading: true });
    try {
      await SuppliersClient.deleteSupplier(supplier_id);
      // Soft delete - remove from active suppliers list
      set((state) => ({
        ...state,
        suppliers: state.suppliers.filter((s) => s.id !== supplier_id),
        error: null,
      }));
    } catch (error) {
      console.error("Error deleting supplier:", error);
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },

  async findOrCreateSupplier(supplierData: {
    organization_id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    external_id?: string | null;
    supplier_type?: SupplierType;
    metadata?: Record<string, string | number | boolean | null | undefined>;
  }): Promise<ISupplier> {
    try {
      const supplier = await SuppliersClient.findOrCreateSupplier(supplierData);

      // Update store with the supplier (whether it was found or created)
      set((state) => {
        const existingIndex = state.suppliers.findIndex(
          (s) => s.id === supplier.id
        );
        if (existingIndex >= 0) {
          // Update existing supplier in store
          const updatedSuppliers = [...state.suppliers];
          updatedSuppliers[existingIndex] = supplier;
          return {
            ...state,
            suppliers: updatedSuppliers,
            error: null,
          };
        } else {
          // Add new supplier to store
          return {
            ...state,
            suppliers: [supplier, ...state.suppliers],
            error: null,
          };
        }
      });

      return supplier;
    } catch (error) {
      console.error("Error finding or creating supplier:", error);
      set({ error: error as Error });
      throw error;
    }
  },

  async searchSuppliers(organization_id: string, searchTerm: string) {
    try {
      const suppliers = await SuppliersClient.searchSuppliers(
        organization_id,
        searchTerm
      );
      return suppliers;
    } catch (error) {
      console.error("Error searching suppliers:", error);
      set({ error: error as Error });
      return [];
    }
  },

  async getSuppliersWithOrderStats(organization_id: string) {
    set({ loading: true });
    try {
      const suppliers = await SuppliersClient.getSuppliersWithOrderStats(
        organization_id
      );
      // Replace store with fresh supplier data (without order stats in the main store)
      // This ensures we don't have stale data from deleted suppliers
      const updatedSuppliers = suppliers.map((supplierWithStats) => {
        const { ...supplier } = supplierWithStats;
        return supplier;
      });

      set({
        suppliers: updatedSuppliers,
        error: null,
      });

      return suppliers;
    } catch (error) {
      console.error("Error fetching suppliers with order stats:", error);
      set({ error: error as Error });
      return [];
    } finally {
      set({ loading: false });
    }
  },
}));
